import type { ImageProps } from 'antd';
import { Image } from 'antd';
import { useEffect, useState } from 'react';
import authenticationApi from 'apis/authentication';

/** 判断是否需要通过 image-proxy 代理访问（AI 服务图片需携带 X-API-Key） */
const needsProxy = (url: string): boolean => {
  if (!url || url.startsWith('blob:')) return false;
  return url.includes('identify-api.ginzaxiaoma.com');
};

/**
 * 替代 antd Image 组件，自动对需要鉴权的 AI 服务图片走 image-proxy 代理，
 * 通过 blob URL 渲染，其余 URL 直接透传。
 */
const ProxiedImage = ({ src, style, ...props }: ImageProps) => {
  const [resolvedSrc, setResolvedSrc] = useState<string>(
    needsProxy(src || '') ? '' : (src || ''),
  );
  const [proxying, setProxying] = useState(needsProxy(src || ''));

  useEffect(() => {
    if (!src) {
      setResolvedSrc('');
      setProxying(false);
      return;
    }
    if (!needsProxy(src)) {
      setResolvedSrc(src);
      setProxying(false);
      return;
    }

    setProxying(true);
    let blobUrl = '';
    let unmounted = false;
    authenticationApi
      .imageProxy(src)
      .then((blob) => {
        blobUrl = URL.createObjectURL(blob);
        if (unmounted) {
          URL.revokeObjectURL(blobUrl);
          return;
        }
        setResolvedSrc(blobUrl);
        setProxying(false);
      })
      .catch(() => {
        if (!unmounted) {
          setResolvedSrc('');
          setProxying(false);
        }
      });

    return () => {
      unmounted = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [src]);

  return (
    <Image
      src={resolvedSrc}
      style={{ ...style, visibility: proxying ? 'hidden' : undefined }}
      {...props}
    />
  );
};

export default ProxiedImage;